import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchTaskWorklist } from "../../../api/tasks/tasksApi";
import {
  TaskListItem,
  TaskWorklistFilters,
} from "../../../api/tasks/taskTypes";
import { OrganizationType } from "../../../enums/organizationTypes";
import { generateTasksTableData } from "../table/tasksTableData";

/** Window fetched from the server; CustomTable paginates client-side within it. */
const WORKLIST_LIMIT = 200;

interface UseTasksDataArgs {
  /** Initial search text (e.g. seeded from the URL). */
  initialSearch?: string;
  /** Initial server-side filters (e.g. seeded from the URL). */
  initialFilters?: Partial<TaskWorklistFilters>;
}

interface UseTasksDataResult {
  tableData: Record<string, any>[];
  items: TaskListItem[];
  loading: boolean;
  totalCount: number;
  searchText: string;
  handleSearch: (text: string) => void;
  applyFilters: (filters: Partial<TaskWorklistFilters>) => void;
  refetch: () => void;
  updateItemStatus: (taskRowId: string, taskStatus: string) => void;
}

/**
 * Fetch + map the pharmacy Tasks worklist, re-fetching (server-side) whenever the
 * search text or applied filters change. Aborts the in-flight request on change.
 *
 * @param args - Optional initial search text and filters (seeded from the URL)
 * @returns Table rows, raw items, loading/total, and search/filter/refetch controls
 */
export const useTasksData = ({
  initialSearch = "",
  initialFilters = {},
}: UseTasksDataArgs = {}): UseTasksDataResult => {
  const [items, setItems] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>(initialSearch);
  const [filters, setFilters] =
    useState<Partial<TaskWorklistFilters>>(initialFilters);
  const [reloadToken, setReloadToken] = useState<number>(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    fetchTaskWorklist(
      {
        org_id: OrganizationType.ASTERA_PRIMARY_CARE,
        page: 1,
        limit: WORKLIST_LIMIT,
        search: searchText.trim() || null,
        filters,
        sort_by: "last_activity_at",
        sort_ascending: false,
      },
      controller.signal,
    )
      .then((response) => {
        setItems(response.items);
        setTotalCount(response.total_count);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setItems([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [searchText, filters, reloadToken]);

  const tableData = useMemo(() => generateTasksTableData(items), [items]);

  const handleSearch = useCallback((text: string) => setSearchText(text), []);
  const applyFilters = useCallback(
    (next: Partial<TaskWorklistFilters>) => setFilters(next),
    [],
  );
  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);
  // Patch a single row's status locally so the worklist reflects a status
  // writeback without a full re-fetch (the backend already mirrored it).
  const updateItemStatus = useCallback(
    (taskRowId: string, taskStatus: string) =>
      setItems((prev) =>
        prev.map((item) =>
          item.id === taskRowId ? { ...item, task_status: taskStatus } : item,
        ),
      ),
    [],
  );

  return {
    tableData,
    items,
    loading,
    totalCount,
    searchText,
    handleSearch,
    applyFilters,
    refetch,
    updateItemStatus,
  };
};
